import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { CurrencyAmount } from '@x-swap-protocol/sdk-core'
import MigrationXttJson from 'abis/migration-xtt.json'
import { MigrationXtt } from 'abis/types'
import { ButtonGray, ButtonLight, ButtonPrimary } from 'components/Button'
import { Input as NumericalInput } from 'components/NumericalInput'
import { darken } from 'polished'
import { useCallback, useMemo } from 'react'
import { ArrowDown } from 'react-feather'
import styled, { useTheme } from 'styled-components/macro'
import { flexColumnNoWrap, flexRowNoWrap } from 'theme/styles'

import { useToggleAccountDrawer } from '../../components/AccountDrawer'
import { AutoColumn } from '../../components/Column'
import { OpacityHoverState } from '../../components/Common'
import { FiatValue } from '../../components/CurrencyInputPanel/FiatValue'
import { LoadingOpacityContainer, loadingOpacityMixin } from '../../components/Loader/styled'
import CurrencyLogo from '../../components/Logo/CurrencyLogo'
import { RowBetween, RowFixed } from '../../components/Row'
import { ArrowWrapper, Dots } from '../../components/swap/styleds'
import { getChainInfo } from '../../constants/chainInfo'
import { SupportedChainId } from '../../constants/chains'
import { XSP_XDC, XTT_XDC } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useContract } from '../../hooks/useContract'
import { useSwitchChain } from '../../hooks/useSwitchChain'
import { useUSDPrice } from '../../hooks/useUSDPrice'
import { useTokenBalancesWithLoadingIndicator } from '../../state/connection/hooks'
import { useAppSelector } from '../../state/hooks'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionType } from '../../state/transactions/types'
import { ThemedText } from '../../theme'
import { Z_INDEX } from '../../theme/zIndex'
import { formatCurrencyAmount } from '../../utils/formatCurrencyAmount'
import { didUserReject } from '../../utils/swapErrorToUserReadableMessage'
import { ArrowContainer } from '../Swap'

const BaseLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 16px 52px;

  justify-content: center;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.sm}px) {
    gap: 16px;
    padding: 100px 8px 52px;
  }
  // @media screen and (min-width: ${({ theme }) => theme.breakpoint.md}px) {
  //   gap: 40px;
  //   padding: 48px 20px;
  // }
  // @media screen and (min-width: ${({ theme }) => theme.breakpoint.xl}px) {
  //   gap: 60px;
  // }
`
const LeftPanel = styled.div`
  flex: 1;
  max-width: 780px;
  min-width: 360px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
`
const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 360px;
  padding: 10px;

  // @media screen and (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
  //   display: flex;
  // }
`
const InputPanel = styled.div<{ hideInput?: boolean }>`
  ${flexColumnNoWrap};
  position: relative;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  z-index: 1;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  transition: height 1s ease;
  will-change: height;
`

const FixedContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`

const Container = styled.div<{ hideInput: boolean }>`
  min-height: 44px;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
`

const CurrencySelect = styled(ButtonGray)<{
  visible: boolean
  selected: boolean
  hideInput?: boolean
  disabled?: boolean
}>`
  align-items: center;
  background-color: ${({ selected, theme }) => (selected ? theme.backgroundInputPanel : theme.blue400)};
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
  box-shadow: ${({ selected }) => (selected ? 'none' : '0px 6px 10px rgba(0, 0, 0, 0.075)')};
  color: ${({ selected, theme }) => (selected ? theme.textPrimary : theme.white)};
  cursor: pointer;
  height: unset;
  border-radius: 22px;
  outline: none;
  user-select: none;
  border: none;
  font-size: 16px;
  font-weight: 500;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  padding: ${({ selected }) => (selected ? '3px 5px' : '4px 7px')};
  gap: 8px;
  justify-content: space-between;
  margin-left: ${({ hideInput }) => (hideInput ? '0' : '12px')};

  &:hover,
  &:active {
    background-color: ${({ theme, selected }) => (selected ? theme.backgroundInteractive : '#5F60D4')};
  }

  &:before {
    background-size: 100%;
    border-radius: inherit;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    content: '';
  }

  &:hover:before {
    background-color: ${({ theme }) => theme.stateOverlayHover};
  }

  &:active:before {
    background-color: ${({ theme }) => theme.stateOverlayPressed};
  }

  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`

const InputRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  justify-content: space-between;
`

const LabelRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.75rem;
  line-height: 1rem;

  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.textSecondary)};
  }
`

const FiatRow = styled(LabelRow)`
  justify-content: flex-end;
  min-height: 20px;
  padding: 8px 0px 0px 0px;
`

const Aligner = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StyledTokenName = styled.span<{ active?: boolean }>`
  margin-left: 5px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  font-size: ${({ active }) => (active ? '18px' : '16px')};
  line-height: ${({ active }) => (active ? '24px' : '26px')};
`

const StyledBalanceMax = styled.button<{ disabled?: boolean }>`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.purple};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
  padding: 4px 6px;
  pointer-events: ${({ disabled }) => (!disabled ? 'initial' : 'none')};

  :hover {
    opacity: ${({ disabled }) => (!disabled ? 0.8 : 0.4)};
  }

  :focus {
    outline: none;
  }
`

const StyledNumericalInput = styled(NumericalInput)<{ $loading: boolean }>`
  ${loadingOpacityMixin};
  text-align: left;
  font-size: 36px;
  line-height: 44px;
  font-variant: small-caps;
`

const SwapSection = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 12px;
  padding: 16px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;

  &:before {
    box-sizing: border-box;
    background-size: 100%;
    border-radius: inherit;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    pointer-events: none;
    content: '';
    border: 1px solid ${({ theme }) => theme.backgroundModule};
  }

  &:hover:before {
    border-color: ${({ theme }) => theme.stateOverlayHover};
  }

  &:focus-within:before {
    border-color: ${({ theme }) => theme.stateOverlayPressed};
  }
`

const SwapWrapper = styled.main<{ chainId?: number }>`
  position: relative;
  background: ${({ theme }) => theme.backgroundSurface};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 8px;
  padding-top: 12px;
  z-index: ${Z_INDEX.default};
  transition: transform 250ms ease;

  &:hover {
    border: 1px solid ${({ theme }) => theme.backgroundOutline};
  }
`

const Link = styled.a`
  color: ${({ theme }) => theme.textPrimary};

  ${OpacityHoverState}
`

const MigrationContractAddress = '0x49F77CE8aa863E43D238d6e0819556d15a59Fedc'

export default function MergeXTT() {
  const theme = useTheme()
  const addTransaction = useTransactionAdder()
  const { account, chainId, connector } = useWeb3React()
  const switchChain = useSwitchChain()
  const toggleWalletDrawer = useToggleAccountDrawer()
  const switchingChain = useAppSelector((state) => state.wallets.switchingChain)

  const xttCurrency = useCurrency(XTT_XDC.address)
  const xspCurrency = useCurrency(XSP_XDC.address)

  const [balances, isLoading] = useTokenBalancesWithLoadingIndicator(account ?? undefined, [XTT_XDC, XSP_XDC])

  const xttBalance = useMemo(() => {
    if (!balances || !balances[XTT_XDC.address]) {
      return undefined
    }
    return balances[XTT_XDC.address]
  }, [balances])

  const [approval, approveCallback] = useApproveCallback(xttBalance, MigrationContractAddress)
  const contract = useContract<MigrationXtt>(MigrationContractAddress, MigrationXttJson.abi)

  const xspBalance = useMemo(() => {
    if (!balances || !balances[XSP_XDC.address]) {
      return undefined
    }
    return balances[XSP_XDC.address]
  }, [balances])

  const chainAllowed = useMemo(() => {
    return chainId === SupportedChainId.XDC
  }, [chainId])

  const xttValue = useMemo(() => {
    if (isLoading || !balances) return ''
    return balances[XTT_XDC.address]?.toFixed(3) ?? ''
  }, [balances, isLoading])

  const xspValue = useMemo(() => {
    if (isLoading || !balances) return ''

    return xttBalance?.multiply(2).toExact() ?? ''
  }, [balances, isLoading, xttBalance])

  const fiatValueXTT = useUSDPrice(xttBalance)
  const fiatValueXSP = useUSDPrice(CurrencyAmount.fromRawAmount(XSP_XDC, xttBalance?.multiply(2)?.quotient ?? '0'))
  const handleMigrate = useCallback(async () => {
    if (!contract || !xttBalance) {
      return
    }

    try {
      const response = await contract.convert(xttBalance.quotient.toString())
      if (response) {
        addTransaction(response, {
          type: TransactionType.MIGRATE_XTT,
          xttAmount: xttBalance.quotient.toString(),
          xttAddress: XTT_XDC.address,
          xspAmount: xttBalance.multiply(2).quotient.toString(),
          xspAddress: XSP_XDC.address,
        })
      }
    } catch (e) {
      console.log(e)
    }
  }, [contract, xttBalance, addTransaction])

  const handleApproveAndMigrate = useCallback(async () => {
    await approveCallback()
    await handleMigrate()
  }, [approveCallback, handleMigrate])

  return (
    <BaseLayout>
      <LeftPanel>
        <ThemedText.DeprecatedLargeHeader>
          <Trans>XTT to XSP Token Migration</Trans>
        </ThemedText.DeprecatedLargeHeader>
        <ThemedText.DeprecatedBody style={{ marginBottom: 8, fontWeight: 400, opacity: 0.8 }}>
          <Trans>
            We’re excited to announce the migration from <b>XTT</b> to <b>XSP</b>! <br />
            This upgrade brings enhanced features, improved utility, and a stronger ecosystem. Below are the details you
            need to participate.
          </Trans>
        </ThemedText.DeprecatedBody>
        <ThemedText.DeprecatedMediumHeader>
          <Trans>Migration Details</Trans>
        </ThemedText.DeprecatedMediumHeader>
        <ThemedText.DeprecatedBody style={{ marginBottom: 8, fontWeight: 400 }}>
          <ul>
            <li>
              <b>Migration Details Conversion Rate: 1 XTT = 2 XSP</b> for the whole period.
            </li>
            <li>
              <b>Migration Period: 22 Apr 2025</b> to <b>1 May 2026</b>
            </li>
            <li>
              All XTT on <b>CEX, Farming</b> and <b>Staking</b> platforms need to be withdrawn and migrated manually.
            </li>
            <li>
              <b>Migration is supported only on this page.</b>
            </li>
            <li>
              Migration is <b>mandatory</b> — the <b>XTT</b> token will no longer be supported.
            </li>
          </ul>
        </ThemedText.DeprecatedBody>
        <ThemedText.DeprecatedBody style={{ marginBottom: 8, fontWeight: 400 }}>
          <Link
            href="https://medium.com/@XSwap_Protocol/xtt-and-xsp-tokens-to-merge-transitioning-to-a-unified-ecosystem-60fbe0b500d2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Why migrate?
          </Link>
        </ThemedText.DeprecatedBody>
      </LeftPanel>
      <RightPanel>
        <SwapWrapper>
          <SwapSection>
            <InputPanel>
              <Container hideInput={false}>
                <InputRow>
                  <StyledNumericalInput
                    className="token-amount-input"
                    value={xttValue}
                    onUserInput={() => null}
                    disabled={!chainAllowed}
                    $loading={isLoading}
                  />

                  <CurrencySelect
                    disabled={true}
                    visible={xttCurrency !== undefined}
                    selected={!!xttCurrency}
                    hideInput={false}
                    onClick={() => null}
                  >
                    <Aligner>
                      <RowFixed>
                        <CurrencyLogo style={{ marginRight: '2px' }} currency={xttCurrency} size="24px" />

                        <StyledTokenName
                          className="token-symbol-container"
                          active={Boolean(xttCurrency && xttCurrency.symbol)}
                        >
                          {(xttCurrency && xttCurrency.symbol && xttCurrency.symbol.length > 20
                            ? xttCurrency.symbol.slice(0, 4) +
                              '...' +
                              xttCurrency.symbol.slice(xttCurrency.symbol.length - 5, xttCurrency.symbol.length)
                            : xttCurrency?.symbol) || <Trans>Select token</Trans>}
                        </StyledTokenName>
                      </RowFixed>
                    </Aligner>
                  </CurrencySelect>
                </InputRow>
                <FiatRow>
                  <RowBetween>
                    <LoadingOpacityContainer $loading={isLoading}>
                      {fiatValueXTT && <FiatValue fiatValue={fiatValueXTT} />}
                    </LoadingOpacityContainer>
                    {account ? (
                      <RowFixed style={{ height: '30px' }}>
                        <ThemedText.DeprecatedBody
                          color={theme.textSecondary}
                          fontWeight={400}
                          fontSize={14}
                          style={{ display: 'inline', color: theme.textTertiary }}
                        >
                          {xttCurrency && xttBalance ? (
                            <Trans>Balance: {formatCurrencyAmount(xttBalance, 4)}</Trans>
                          ) : null}
                        </ThemedText.DeprecatedBody>
                      </RowFixed>
                    ) : (
                      <span />
                    )}
                  </RowBetween>
                </FiatRow>
              </Container>
            </InputPanel>
          </SwapSection>
          <ArrowWrapper clickable={false}>
            <ArrowContainer
              data-testid="swap-currency-button"
              onClick={() => {
                null
              }}
              color={theme.textPrimary}
            >
              <ArrowDown size="16" color={theme.textPrimary} />
            </ArrowContainer>
          </ArrowWrapper>
          <AutoColumn gap="sm">
            <SwapSection>
              <InputPanel>
                <Container hideInput={false}>
                  <InputRow>
                    <StyledNumericalInput
                      className="token-amount-input"
                      value={xspValue}
                      onUserInput={() => null}
                      disabled={!chainAllowed}
                      $loading={isLoading}
                    />

                    <CurrencySelect
                      disabled={true}
                      visible={xspCurrency !== undefined}
                      selected={!!xspCurrency}
                      hideInput={false}
                      onClick={() => null}
                    >
                      <Aligner>
                        <RowFixed>
                          <CurrencyLogo style={{ marginRight: '2px' }} currency={xspCurrency} size="24px" />

                          <StyledTokenName
                            className="token-symbol-container"
                            active={Boolean(xspCurrency && xspCurrency.symbol)}
                          >
                            {(xspCurrency && xspCurrency.symbol && xspCurrency.symbol.length > 20
                              ? xspCurrency.symbol.slice(0, 4) +
                                '...' +
                                xspCurrency.symbol.slice(xspCurrency.symbol.length - 5, xspCurrency.symbol.length)
                              : xspCurrency?.symbol) || <Trans>Select token</Trans>}
                          </StyledTokenName>
                        </RowFixed>
                      </Aligner>
                    </CurrencySelect>
                  </InputRow>
                  <FiatRow>
                    <RowBetween>
                      <LoadingOpacityContainer $loading={isLoading}>
                        {fiatValueXSP && <FiatValue fiatValue={fiatValueXSP} />}
                      </LoadingOpacityContainer>
                      {account ? (
                        <RowFixed style={{ height: '30px' }}>
                          <ThemedText.DeprecatedBody
                            color={theme.textSecondary}
                            fontWeight={400}
                            fontSize={14}
                            style={{ display: 'inline', color: theme.textTertiary }}
                          >
                            {xspCurrency && xspBalance ? (
                              <Trans>Balance: {formatCurrencyAmount(xspBalance, 4)}</Trans>
                            ) : null}
                          </ThemedText.DeprecatedBody>
                        </RowFixed>
                      ) : (
                        <span />
                      )}
                    </RowBetween>
                  </FiatRow>
                </Container>
              </InputPanel>
            </SwapSection>
            <div>
              {switchingChain ? (
                <ButtonPrimary disabled={true}>
                  <Trans>Connecting to {getChainInfo(switchingChain)?.label}</Trans>{' '}
                </ButtonPrimary>
              ) : !account ? (
                <ButtonLight onClick={toggleWalletDrawer} fontWeight={600}>
                  <Trans>Connect Wallet</Trans>
                </ButtonLight>
              ) : chainId && chainId !== SupportedChainId.XDC ? (
                <ButtonPrimary
                  onClick={async () => {
                    try {
                      await switchChain(connector, SupportedChainId.XDC)
                    } catch (error) {
                      if (didUserReject(error)) {
                        // Ignore error, which keeps the user on the previous chain.
                      } else {
                        // TODO(WEB-3306): This UX could be improved to show an error state.
                        throw error
                      }
                    }
                  }}
                >
                  Connect to {getChainInfo(SupportedChainId.XDC)?.label}
                </ButtonPrimary>
              ) : approval === ApprovalState.NOT_APPROVED ? (
                <ButtonPrimary onClick={handleApproveAndMigrate}>Approve XTT & Migrate</ButtonPrimary>
              ) : approval === ApprovalState.PENDING ? (
                <ButtonPrimary disabled>
                  Approving <Dots />
                </ButtonPrimary>
              ) : (
                <ButtonPrimary disabled={xttBalance?.lessThan('1')} onClick={handleMigrate}>
                  Migrate
                </ButtonPrimary>
              )}
            </div>
          </AutoColumn>
        </SwapWrapper>
      </RightPanel>
    </BaseLayout>
  )
}
